import {
    Box,
    IconButton,
    Modal,
    Typography,
    Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import TableContainer from "../../Components/TableContainer";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import CustomTextField from "../../Components/CustomTextField";
import CustomButton from "../../Components/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";

const HashtagManagement = () => {
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [hashtagData, setHashtagData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [editingHashtag, setEditingHashtag] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            hashtagName: "",
        },
        validationSchema: Yup.object({
            hashtagName: Yup.string()
                .required("Please enter hashtag name")
                .max(50, "Hashtag name cannot be more than 50 characters")
                .matches(/^[a-zA-Z0-9_]+$/, "Only letters, numbers and underscores allowed"),
        }),
        onSubmit: async (values, { resetForm }) => {
            setSubmitLoading(true);
            try {
                const apiEndpoint = editingHashtag
                    ? `${ApiList.HASHTAG_UPDATE}/${editingHashtag.id}`
                    : ApiList.HASHTAG_CREATE;

                const response = await AxiosInstance.post(apiEndpoint, {
                    tag: values.hashtagName,
                });

                if (response?.data?.s === 1) {
                    toast.success(
                        editingHashtag
                            ? "Hashtag updated successfully"
                            : "Hashtag created successfully"
                    );
                    resetForm();
                    setEditingHashtag(null);
                    handleCloseModal();
                    fetchHashtagList();
                } else {
                    toast.error(response?.data?.m || "Something went wrong");
                }
                setSubmitLoading(false);
            } catch (error) {
                console.error("Error saving hashtag:", error);
                toast.error("Failed to save hashtag");
                setSubmitLoading(false);
            }
        },
    });

    const getHashtagList = async (params) => {
        setLoading(true);
        try {
            const response = await AxiosInstance.post(ApiList.USER_GET, params);
            if (response?.data?.s === 1) {
                const formattedHashtags = response?.data?.r?.map((hashtag, index) => ({
                    rowid: index + 1 + page * pageSize,
                    ...hashtag,
                }));
                setHashtagData(formattedHashtags);
                if (params.skip === 0) {
                    const totalCount = response?.data?.total || 0;
                    localStorage.setItem("hashtagTotalCount", totalCount);
                    setTotalData(totalCount);
                } else {
                    const totalCountFromStorage = localStorage.getItem("hashtagTotalCount");
                    setTotalData(
                        totalCountFromStorage ? Number(totalCountFromStorage) : 0
                    );
                }
                setLoading(false);
            } else {
                setHashtagData([]);
                setTotalData(0);
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching hashtag data:", error);
            setLoading(false);
        }
    };

    const fetchHashtagList = () => {
        const skip = page * pageSize;
        const params = {
            select: "id,tag",
            from: "tags",
            arg: "status = 1",
            order: "id DESC",
            skip,
            limit: pageSize,
            total: 1,
        };
        getHashtagList(params);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchHashtagList();
    }, [page]);

    const handleOpenModal = (hashtag = null) => {
        if (hashtag) {
            setEditingHashtag(hashtag);
            formik.setFieldValue("hashtagName", hashtag.tag || "");
        } else {
            setEditingHashtag(null);
            formik.resetForm();
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingHashtag(null);
        formik.resetForm();
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this hashtag?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#359c41ff",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
            background: "#fff",
            color: "#fff",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await AxiosInstance.post(
                        `${ApiList.HASHTAG_DELETE}/${id}`,
                        { status: "0"}
                    );
                    if (response?.data?.s === 1) {
                        toast.success("Hashtag deleted successfully");
                        fetchHashtagList();
                    } else {
                        toast.error(response?.data?.m || "Failed to delete hashtag");
                    }
                } catch (error) {
                    console.error("Error deleting hashtag:", error);
                    toast.error("Failed to delete hashtag");
                }
            }
        });
    };

    const columns = [
        {
            field: "rowid",
            headerName: "Sr.No",
            width: 100,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "hashtag_name",
            headerName: "Hashtag Name",
            flex: 1,
            minWidth: 300,
            headerClassName: "leftAlignHeader",
            cellClassName: "leftAlign",
            renderCell: (params) => {
                const hashtagName = params?.row?.tag;
                const displayName = hashtagName?.startsWith("#") ? hashtagName : `#${hashtagName}`;
                return (
                    <Tooltip title={displayName}>
                        <Typography>{displayName}</Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 150,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                        onClick={() => handleOpenModal(params.row)}
                        sx={{
                            color: "#359c41ff",
                            "&:hover": { backgroundColor: "rgba(59, 198, 241, 0.1)" },
                        }}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => handleDelete(params.row.id)}
                        sx={{
                            color: "#ff4444",
                            "&:hover": { backgroundColor: "rgba(255, 68, 68, 0.1)" },
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "100vh",
                    background: "#B9CC0310",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        p: 2,
                        background: "#B9CC0310",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: "18px", md: "28px" },
                            fontWeight: "300",
                            color: "#000",
                        }}
                    >
                        Hashtag Management
                    </Typography>
                    <CustomButton
                        title="Add Hashtag"
                        Icon={<AddIcon />}
                        onClick={() => handleOpenModal()}
                        sx={{ width: "auto", px: 3 }}
                    />
                </Box>
                <Box sx={{ width: "100%", height: "calc(100vh - 74px)" }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            p: 1,
                            overflow: "scroll",
                        }}
                    >
                        <TableContainer
                            columns={columns}
                            rows={hashtagData}
                            handlePageChange={handlePageChange}
                            page={page}
                            loading={loading}
                            totalData={totalData}
                            pageSize={pageSize}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Add/Edit Modal */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                BackdropProps={{
                    sx: {
                        backdropFilter: "blur(5px)",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "90%",
                        maxWidth: "500px",
                        bgcolor: "#fff",
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 2,
                        outline: "none",
                    }}
                >
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                        }}
                    >
                        <CloseIcon sx={{ color: "#000" }} />
                    </IconButton>
                    <Typography
                        sx={{
                            fontSize: "24px",
                            fontWeight: "400",
                            color: "#000",
                            mb: 3,
                        }}
                    >
                        {editingHashtag ? "Edit Hashtag" : "Add New Hashtag"}
                    </Typography>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <CustomTextField
                                placeholder="Enter hashtag name (without #)"
                                label="Hashtag Name"
                                type="text"
                                name="hashtagName"
                                value={formik.values.hashtagName}
                                onBlur={formik.handleBlur}
                                onKeyUp={formik.handleBlur}
                                errors={formik.touched.hashtagName && formik.errors.hashtagName}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    if (value.trimStart() !== value) {
                                        e.target.value = value.trimStart();
                                    }
                                    formik.handleChange(e);
                                }}
                            />
                            <CustomButton
                                title={editingHashtag ? "Update" : "Submit"}
                                type="submit"
                                loading={submitLoading}
                            />
                        </Box>
                    </form>
                </Box>
            </Modal>
        </>
    );
};

export default HashtagManagement;
